/**
 * 68. Employee Directory — org chart / list; search by
 * role/department/status.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList, useApiQuery } from "@/lib/api/hooks";
import { CreateRecordButton } from "@/components/CreateRecordButton";

interface EmployeeRow {
  id: string;
  employeeNumber: string;
  displayName: string;
  email: string;
  department: string;
  jobTitle: string;
  manager: string | null;
  status: "Active" | "OnLeave" | "Suspended" | "Terminated";
}

interface ManagerOption {
  id: string;
  displayName?: string;
  employeeNumber?: string;
}

export function EmployeeDirectoryPage() {
  // The people someone could report to are the employees already on file.
  const managers = useApiQuery<ManagerOption[]>(
    ["hr", "employees", "managers"],
    "/hr/employees?limit=200",
  );
  const { data, isLoading } = useApiList<EmployeeRow>(
    ["hr", "employees"],
    "/hr/employees",
  );

  return (
    <div>
      <PageHeader
        title="Employee Directory"
        subtitle="Org chart / list; search by role, department, or status."
        actions={
          <CreateRecordButton
            label="Invite employee"
            path="/hr/employees"
            invalidate={["hr", "employees"]}
            fields={[
              { name: "employeeNo", label: "Employee number", required: true },
              { name: "firstName", label: "First name", required: true },
              { name: "lastName", label: "Last name", required: true },
              { name: "email", label: "Work email" },
              {
                name: "managerId",
                label: "Reports to",
                type: "select",
                hint: "Leave blank for someone at the top of the organisation.",
                options: (managers.data ?? []).map((m) => ({
                  value: m.id,
                  label: m.displayName ?? m.employeeNumber ?? m.id,
                })),
              },
            ]}
          />
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Employees (${data.total})`}>
          <DataTable<EmployeeRow>
            rows={data.items}
            columns={[
              {
                key: "employeeNumber",
                label: "Number",
                render: (r) => (
                  <span className="atlas-mono">{r.employeeNumber}</span>
                ),
              },
              { key: "displayName", label: "Name" },
              { key: "email", label: "Email" },
              { key: "jobTitle", label: "Job title" },
              { key: "department", label: "Department" },
              {
                key: "manager",
                label: "Manager",
                render: (r) => r.manager ?? "—",
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Active" ? (
                    <StatusBadge status={r.status} />
                  ) : (
                    <Badge
                      tone={
                        r.status === "OnLeave"
                          ? "warning"
                          : r.status === "Terminated"
                            ? "danger"
                            : "default"
                      }
                    >
                      {r.status}
                    </Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
